import { Link } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { Flame, Layers, Music2, Sparkles, Tags, Zap } from 'lucide-react';
import { api, resolveApiMediaUrl } from '../services/apiClient';
import { useAuth } from '../features/auth/AuthContext';
import { dedupeCanonicalAssetGroups } from '../features/catalog/canonicalCatalog';
import { useCanonicalCatalog } from '../features/catalog/useCanonicalCatalog';

const featuredGenres = [
  'Reggaeton',
  'Hip-Hop',
  'EDM',
  'Cumbia',
  'Regional Mexicano',
  'Bachata',
  'Merengue',
  'Dembow',
  'House',
  'Latin Dance-EDM',
  'Pop',
  'AND MANY MORE'
];

const versionFeatures = [
  { icon: Music2, title: 'Clean & Dirty', text: 'Radio-ready and club-ready edits for every set.' },
  { icon: Zap, title: 'Party Starters', text: 'Quick energy builders for transitions and open-format moments.' },
  { icon: Layers, title: 'Transitions', text: 'DJ-friendly versions built for smoother mixing.' },
  { icon: Sparkles, title: 'Remixes', text: 'PrimeTime edits and versions made to stand out.' }
];

const launchDate = new Date('2026-12-18T00:00:00');
const HOME_VIDEO_PANEL_LIMIT = 10;
const HOME_MOBILE_NEW_RELEASES_LIMIT = 2;
const HOME_MOBILE_HOT_TRACKS_LIMIT = 4;

interface HomeTrack {
  id?: string;
  trackId: string;
  title: string;
  artist: string;
  artworkUrl?: string | null;
  imageUrl?: string | null;
  previewVideoUrl?: string | null;
  totalDownloads?: number;
}

function getHomeMobileCardClass(index: number, mobileLimit: number) {
  return index < mobileLimit ? '' : 'hidden sm:block';
}

function VideoShowcaseCard({ track, className = '' }: { track: HomeTrack; className?: string }) {
  const [previewFailed, setPreviewFailed] = useState(false);
  const displayLabel = `${track.artist} - ${track.title}`;
  const previewUrl = resolveApiMediaUrl(track.previewVideoUrl);
  const artworkUrl = track.artworkUrl || track.imageUrl || '/img/logo-primetime.png';

  return (
    <article className={`motion-reveal-card glass relative aspect-[16/10] overflow-hidden rounded-2xl ${className}`}>
      <div className="absolute inset-0 overflow-hidden rounded-2xl bg-black">
        {previewUrl && !previewFailed ? (
          <video
            src={previewUrl}
            poster={artworkUrl}
            className="pointer-events-none absolute inset-0 h-full w-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            controlsList="nodownload noplaybackrate noremoteplayback"
            aria-hidden="true"
            onError={() => setPreviewFailed(true)}
          />
        ) : (
          <img
            src={artworkUrl}
            alt=""
            className="h-full w-full object-cover"
          />
        )}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/75 to-transparent px-3 pb-2 pt-6">
          <h3 className="truncate text-xs font-bold text-white sm:text-sm" title={displayLabel}>
            {displayLabel}
          </h3>
        </div>
      </div>
    </article>
  );
}

function VideoShowcasePlaceholder({ label, position, className = '' }: { label: string; position: number; className?: string }) {
  return (
    <article
      aria-label={`PrimeTime ${label} placeholder ${position}`}
      className={`motion-reveal-card glass relative aspect-[16/10] overflow-hidden rounded-2xl ${className}`}
    >
      <div className="absolute inset-0 overflow-hidden rounded-2xl bg-black bg-red-radial">
        <img
          src="/img/logo-primetime.png"
          alt=""
          className="h-full w-full object-contain p-8 opacity-45"
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/25 text-center">
          <span className="text-[10px] font-black uppercase tracking-[0.24em] text-prime-red">PrimeTime</span>
          <span className="mt-1 text-xs font-black uppercase tracking-wider text-white/70">Coming Soon</span>
        </div>
      </div>
    </article>
  );
}

function getLaunchCountdown() {
  const remainingMs = Math.max(0, launchDate.getTime() - Date.now());
  const totalSeconds = Math.floor(remainingMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return { days, hours, minutes, seconds };
}

export function Home() {
  const { user } = useAuth();
  const { assetGroups: canonicalAssetGroups, status: canonicalCatalogStatus } = useCanonicalCatalog('/api/catalog/teasers');
  const [hotTracks, setHotTracks] = useState<HomeTrack[]>([]);
  const [isLoadingTracks, setIsLoadingTracks] = useState(true);
  const [tracksError, setTracksError] = useState('');
  const [launchCountdown, setLaunchCountdown] = useState(getLaunchCountdown);
  const viewAllPath = user ? '/crates' : '/login';

  const newReleases = useMemo<HomeTrack[]>(() => (
    dedupeCanonicalAssetGroups(canonicalAssetGroups)
      .sort((first, second) => (second.releaseYear ?? -Infinity) - (first.releaseYear ?? -Infinity))
      .slice(0, HOME_VIDEO_PANEL_LIMIT)
      .map((asset) => ({
        trackId: asset.assetGroupId,
        title: asset.title,
        artist: asset.artist,
        artworkUrl: asset.artworkUrl || null,
        previewVideoUrl: `/api/preview/asset-groups/${encodeURIComponent(asset.assetGroupId)}`
      }))
  ), [canonicalAssetGroups]);

  useEffect(() => {
    let active = true;
    setIsLoadingTracks(true);
    setTracksError('');

    api<HomeTrack[]>('/api/tracks/hot').then((hot) => {
      if (!active) return;
      setHotTracks(hot);
    }).catch(() => {
      if (!active) return;
      setHotTracks([]);
      setTracksError('Unable to load tracks right now.');
    }).finally(() => {
      if (active) setIsLoadingTracks(false);
    });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setLaunchCountdown(getLaunchCountdown());
    }, 1000);

    return () => window.clearInterval(timer);
  }, []);

  return (
    <>
      <section className="container-shell pt-10 pb-12 text-center">
        <div className="mx-auto mb-8 w-full text-center" aria-live="polite">
          <div className="rounded-xl border border-prime-red/35 bg-black/80 p-2 shadow-glow backdrop-blur-md sm:rounded-2xl sm:p-4">
            <p className="text-4xl font-black uppercase tracking-[0.18em] text-prime-red sm:text-6xl sm:tracking-[0.3em]">
              Coming Soon
            </p>
            <p className="text-xl font-black uppercase tracking-[0.24em] text-prime-red drop-shadow-[0_0_12px_rgba(255,0,0,0.7)] sm:text-2xl sm:tracking-[0.35em]">
              Launch Countdown
            </p>
            <p className="mt-1.5 text-lg font-bold uppercase tracking-[0.16em] text-white/55 sm:mt-2 sm:text-xl sm:tracking-[0.22em]">
              December 18, 2026
            </p>
            <div className="mt-2 grid grid-cols-4 gap-1 sm:mt-3 sm:gap-3">
              {[
                ['Days', launchCountdown.days],
                ['Hours', launchCountdown.hours],
                ['Minutes', launchCountdown.minutes],
                ['Seconds', launchCountdown.seconds]
              ].map(([label, value]) => (
                <div key={label} className="min-w-0">
                  <div className="text-lg font-black tabular-nums text-white sm:text-4xl">
                    {String(value).padStart(2, '0')}
                  </div>
                  <div className="mt-0.5 text-[8px] font-black uppercase tracking-wide text-white/50 sm:mt-1 sm:text-xs sm:tracking-wider">
                    {label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="mx-auto max-w-5xl">
          <h1 className="home-intro-copy mt-2 text-4xl font-black tracking-tight sm:text-6xl">
            The Hottest VJ/DJ Edits, <span className="text-prime-red">All In One Place.</span>
          </h1>
          <div className="home-intro-copy mx-auto mt-6 grid w-full max-w-sm grid-cols-2 gap-3 sm:flex sm:max-w-none sm:justify-center">
            {!user && <Link to="/login" className="btn-secondary w-full text-center sm:w-32">Login</Link>}
            {!user && <Link to="/register" className="btn-secondary w-full text-center sm:w-32">Sign Up</Link>}
          </div>
        </div>
        <div className="home-logo-panel glass-red mx-auto mt-8 w-full overflow-hidden rounded-3xl p-2">
          <div className="relative flex h-[300px] flex-col items-center justify-end overflow-hidden rounded-2xl bg-black bg-red-radial px-4 py-2 sm:h-[360px]">
            <div className="home-logo-mark absolute left-1/2 top-1/2 aspect-[3/2] max-h-[460px] w-[90%] max-w-6xl -translate-x-1/2 -translate-y-[58%] scale-[2.18]">
              <img src="/img/logo-primetime2.png" alt="PrimeTime DJ Pool" className="block h-full w-full object-contain" />
            </div>
            <p className="home-logo-message relative z-10 flex max-w-full -translate-y-[180px] flex-col gap-0.5 px-2 text-xs font-bold uppercase leading-tight tracking-normal text-white/70 sm:-translate-y-[216px] sm:text-sm">
              <span className="text-center w-full mb-1">PRIMETIME’S MISSION IS TO PROVIDE DJS AND VJS WITH THE RIGHT TOOLS TO PERFORM AT THEIR BEST.</span>
              <span className="text-center w-full mb-1">STOP DIGGING THROUGH ENDLESS RECORD POOLS.</span>
              <span className="text-center w-full">GET EXCLUSIVE VIDEO EDITS AND REMIXES THAT ARE CROWD-TESTED AND BUILT TO WORK IN REAL DJ SETS.</span>
              <span className="text-center w-full">CURATED BY GENRE, BPM, AND KEY.</span>
              <span className="text-center w-full">READY FOR YOUR NEXT PERFORMANCE.</span>
              <span className="text-center w-full">NO DIGGING. NO STRESS. JUST PRIMETIME.</span>
            </p>
          </div>
        </div>
      </section>

      <section className="motion-section container-shell pb-20 md:pb-28 2xl:max-w-[1536px]">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <div className="flex items-center gap-2 text-prime-red">
              <Sparkles size={20} />
              <h2 className="text-2xl font-black uppercase">NEW RELEASES</h2>
            </div>
            <p className="mt-1 font-bold text-white/80">Newest featured edits.</p>
          </div>
          <Link className="nav-link px-3 py-2" to={viewAllPath}>View All</Link>
        </div>
        {canonicalCatalogStatus === 'loading' && (
          <p className="mb-4 text-sm font-bold text-white/45" role="status">Loading new releases...</p>
        )}
        {(canonicalCatalogStatus === 'unavailable' || canonicalCatalogStatus === 'paywalled') && (
          <p className="mb-4 text-sm font-bold text-white/45" role="status">New release previews are coming soon.</p>
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {Array.from({ length: HOME_VIDEO_PANEL_LIMIT }, (_, index) => {
            const track = newReleases[index];
            const mobileCardClass = getHomeMobileCardClass(index, HOME_MOBILE_NEW_RELEASES_LIMIT);
            return track ? (
              <VideoShowcaseCard key={track.trackId} track={track} className={mobileCardClass} />
            ) : (
              <VideoShowcasePlaceholder key={`new-release-placeholder-${index}`} label="new release" position={index + 1} className={mobileCardClass} />
            );
          })}
        </div>
      </section>

      <section className="motion-section container-shell pb-20 md:pb-28 2xl:max-w-[1536px]">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <div className="flex items-center gap-2 text-prime-red">
              <Flame size={20} />
              <h2 className="text-2xl font-black uppercase">Hot Tracks</h2>
            </div>
            <p className="mt-1 font-bold text-white/80">Most-downloaded video edits on PrimeTime.</p>
          </div>
          <Link className="nav-link px-3 py-2" to={viewAllPath}>View All</Link>
        </div>
        {isLoadingTracks && (
          <p className="mb-4 text-sm font-bold text-white/45" role="status">Loading hot tracks...</p>
        )}
        {tracksError && (
          <p className="mb-4 text-sm font-bold text-white/45" role="status">{tracksError}</p>
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {Array.from({ length: HOME_VIDEO_PANEL_LIMIT }, (_, index) => {
            const track = hotTracks[index];
            const mobileCardClass = getHomeMobileCardClass(index, HOME_MOBILE_HOT_TRACKS_LIMIT);
            return track ? (
              <VideoShowcaseCard key={track.trackId} track={track} className={mobileCardClass} />
            ) : (
              <VideoShowcasePlaceholder key={`hot-track-placeholder-${index}`} label="hot track" position={index + 1} className={mobileCardClass} />
            );
          })}
        </div>
      </section>

      <section className="motion-section container-shell pb-20 md:pb-28">
        <div className="mb-8 flex items-center gap-2 text-prime-red">
          <Tags size={20} />
          <h2 className="text-2xl font-black uppercase">Featured Genres</h2>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {featuredGenres.map((genre) => (
            <article
              key={genre}
              className="motion-reveal-card glass flex items-center rounded-2xl px-5 py-6"
            >
              <p className="text-sm font-black uppercase tracking-wider text-white">{genre}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="motion-section container-shell pb-20 md:pb-28">
        <div className="mb-8 flex items-center gap-2 text-prime-red">
          <Layers size={20} />
          <h2 className="text-2xl font-black uppercase">Versions & Features</h2>
        </div>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {versionFeatures.map((item) => {
            const Icon = item.icon;
            return (
              <article key={item.title} className="motion-reveal-card glass rounded-2xl p-5">
                <Icon className="text-prime-red" size={22} />
                <h3 className="mt-4 font-black uppercase tracking-wide">{item.title}</h3>
                <p className="mt-2 text-sm leading-6 text-white/50">{item.text}</p>
              </article>
            );
          })}
        </div>
      </section>

      <section className="motion-section pb-20 md:pb-28">
        <div className="container-shell">
          <div className="relative min-h-[360px] overflow-hidden rounded-3xl border border-white/10 bg-black shadow-soft md:min-h-[460px]">
            <video
              className="absolute inset-0 h-full w-full object-cover opacity-70"
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
            >
              <source src="/img/reddjvideolloop.mp4" type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/55 to-black/80" />
            <div className="relative z-10 flex min-h-[360px] flex-col items-center justify-center px-6 py-16 text-center md:min-h-[460px]">
              <p className="text-xs font-black uppercase tracking-[0.28em] text-prime-red">Built For Working VJ/DJs</p>
              <h2 className="mt-4 max-w-3xl text-3xl font-black uppercase tracking-tight text-white sm:text-5xl">
                Crowd tested edits ready for your next set.
              </h2>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
